// ─────────────────────────────────────────────────────────────────────────────
// Contract types — what the browser receives from the BFF.
//
// Mirrors services/api-gateway/src/checkout/checkout.service.ts's
// PublicCheckoutResponse, BUT deliberately NOT imported from there.
//
// Why re-declare instead of share?
//   1. Posteering's apps/ folder is outside the npm workspace (see root
//      package.json "workspaces"). There is no workspace-linked import path
//      to @posteering/shared-types from here.
//   2. Even if there were, the BFF itself defines its response locally rather
//      than importing — Session 50 Commit 4 (908a891) made that a deliberate
//      security pattern: the allow-list IS the boundary.
//   3. The browser-side allow-list is a SEPARATE boundary from the BFF's.
//      If the BFF ever grows a field and the frontend doesn't add it here,
//      the frontend silently ignores it — which is the safer default.
//
// Nullability rules (confirmed from checkout.service.ts):
//   - BFF maps `undefined` on the wire to `null` via `?? null` coalescing.
//   - So every optional field arrives as `T | null`, never as missing key.
//   - createdAt and currency are always present.
//   - status is one of exactly five string values.
// ─────────────────────────────────────────────────────────────────────────────

export type CheckoutStatus =
  | 'pending'
  | 'completed'
  | 'expired'
  | 'cancelled'
  | 'failed';

export interface PublicCheckoutResponse {
  status: CheckoutStatus;
  amountKobo: number | null;
  amountDisplay: string | null;           // server-formatted (e.g. "₦61,800.00")
  currency: string;                        // always present
  virtualAccount: string | null;
  virtualAccountName: string | null;
  virtualAccountBank: string | null;
  expiresAt: string | null;                // ISO 8601, null = no expiry / no countdown
  createdAt: string;                       // ISO 8601, always present
  merchantDisplayName: string;             // always present, server-defaulted to 'Posteering'
  returnUrl: string | null;                // null = hide "Back to merchant"
}

// ─────────────────────────────────────────────────────────────────────────────
// Error shapes.
//
// The BFF + SentryExceptionFilter emit { statusCode, error, message } for
// all service-layer HttpExceptions. Routing-layer 404s (malformed token
// that bypasses client-side validation) emit { statusCode, message } with
// NO `error` field — we treat those as CHECKOUT_NOT_FOUND defensively.
//
// Error codes that can reach the browser:
//   - CHECKOUT_NOT_FOUND   (404, terminal, stop polling)
//   - TOO_MANY_REQUESTS    (429, rare, back off hard)
//   - BANKRAIL_UNAVAILABLE (502, transient, keep polling)
//   - BANKRAIL_TIMEOUT     (504, transient, keep polling)
// ─────────────────────────────────────────────────────────────────────────────

export type CheckoutErrorCode =
  | 'CHECKOUT_NOT_FOUND'
  | 'TOO_MANY_REQUESTS'
  | 'BANKRAIL_UNAVAILABLE'
  | 'BANKRAIL_TIMEOUT'
  | 'NETWORK_ERROR'      // browser-side only: fetch threw (offline, DNS, etc.)
  | 'UNKNOWN_ERROR';     // catch-all

export interface CheckoutError {
  code: CheckoutErrorCode;
  message: string;
  httpStatus: number | null;  // null when the error happened before a response
}

// ─────────────────────────────────────────────────────────────────────────────
// Classification helpers — used by the polling loop to decide next action.
// ─────────────────────────────────────────────────────────────────────────────

export function isTerminalStatus(s: CheckoutStatus): boolean {
  return s !== 'pending';
}

export function isTerminalError(e: CheckoutError): boolean {
  // Only CHECKOUT_NOT_FOUND is truly terminal from the browser's POV.
  // Everything else is worth retrying (the intent may resolve later).
  return e.code === 'CHECKOUT_NOT_FOUND';
}
